/**
 * Trade-in money formatting — currency-aware (trade-ins may settle in non-USD),
 * building on the shared cents convention. Amounts are in cents on the wire.
 */
const formatters = new Map<string, Intl.NumberFormat | null>();

/**
 * Returns null for a currency `Intl` will not accept, so the caller can fall back to printing
 * the bare amount beside its raw code.
 *
 * Caching a *USD* formatter under the bad key instead would be worse than not caching: it
 * relabels the amount, permanently. A payout settling in an unrecognised currency would render
 * with a `$` for the rest of the session, and a partner reconciling a trade-in has no way to
 * tell the symbol is wrong — especially here, where the Payout section only shows the currency
 * row when the code is *not* USD, so the page would contradict itself.
 */
const formatterFor = (currency: string): Intl.NumberFormat | null => {
  if (!formatters.has(currency)) {
    try {
      formatters.set(currency, new Intl.NumberFormat('en-US', { style: 'currency', currency }));
    } catch {
      console.error('Unsupported trade-in transaction currency', { currency });
      formatters.set(currency, null);
    }
  }
  return formatters.get(currency) ?? null;
};

/** Format a cents amount in the trade-in's transaction currency (default USD). */
export const formatMoney = (
  cents: number | null | undefined,
  currency: string | null | undefined = 'USD'
): string => {
  if (cents == null) return '—';
  // `|| 'USD'` rather than relying on the default parameter: a default only fires for
  // `undefined`, and the wire sends null.
  const code = currency || 'USD';
  const amount = cents / 100;
  return formatterFor(code)?.format(amount) ?? `${amount.toFixed(2)} ${code}`;
};
